import * as React from 'react'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import TextField from '@mui/material/TextField'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { Box } from '@mui/material'
import { CalendarPickerView } from '@mui/x-date-pickers'
import { Moment } from 'moment'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, useAppSelector } from 'redux/Hook'
import { updateQuestions } from 'redux/slices/SignUpQuestions'
import { DEFAULT_DATE_FORMAT, YEAR_FORMAT } from 'lib/participants/Constants'

type DatePickerType = 'before' | 'after'

const YEAR_ONLY_VIEW: CalendarPickerView[] = ['year']
const DEFAULT_VIEW: CalendarPickerView[] = ['year', 'day']

interface DateQuestion extends SignupFormQuestionModel {
  value: any
}

const DateQuestion = ({ question }: { question: DateQuestion }) => {
  const { questions } = useAppSelector((state) => state.signUpQuestions)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const handleChange = ({
    value,
    type,
  }: {
    value: Moment | null
    type: DatePickerType
  }) => {
    const updatedQuestions = questions.map((savedQuestion) => {
      if (savedQuestion.id === question.id) {
        const previousValue =
          savedQuestion.value?.length && savedQuestion.value[0]

        const formattedValue = {
          ...previousValue,
          name: '',
          [type]: value,
        }

        return {
          ...savedQuestion,
          value: [formattedValue],
          type: question.type,
          yearOnlyDate: question.yearOnlyDate,
          selected: type,
        }
      }

      return savedQuestion
    })

    dispatch(updateQuestions(updatedQuestions as SignUpFilterQuestion[]))
  }

  const questionLabelBefore = `(${t(
    'participant.signUpQuestions.dateBefore',
  )}) ${question.question}`

  const questionLabelAfter = `(${t('participant.signUpQuestions.dateAfter')}) ${
    question.question
  }`

  const datePickerViews = question.yearOnlyDate ? YEAR_ONLY_VIEW : DEFAULT_VIEW

  const inputFormat = question.yearOnlyDate ? YEAR_FORMAT : DEFAULT_DATE_FORMAT

  const getValue = (type: DatePickerType) => {
    return (question.value && question.value[0]?.[type]) || null
  }

  return (
    <Box display="flex">
      <LocalizationProvider
        dateAdapter={AdapterMoment}
        localeText={{
          nextMonth: t('datePicker.nextMonth'),
          previousMonth: t('datePicker.previousMonth'),
        }}
      >
        <DesktopDatePicker
          label={questionLabelBefore}
          inputFormat={inputFormat}
          value={getValue('before')}
          onChange={(selectedDate) =>
            handleChange({ value: selectedDate, type: 'before' })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              sx={{ width: '100%', mr: 1.5 }}
            />
          )}
          views={datePickerViews}
        />
      </LocalizationProvider>

      <LocalizationProvider
        dateAdapter={AdapterMoment}
        localeText={{
          nextMonth: t('datePicker.nextMonth'),
          previousMonth: t('datePicker.previousMonth'),
        }}
      >
        <DesktopDatePicker
          label={questionLabelAfter}
          inputFormat={inputFormat}
          value={getValue('after')}
          onChange={(selectedDate) =>
            handleChange({ value: selectedDate, type: 'after' })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              sx={{ width: '100%', ml: 1.5 }}
            />
          )}
          views={datePickerViews}
        />
      </LocalizationProvider>
    </Box>
  )
}

export default DateQuestion
